import React, {
  useRef, useEffect, useState
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import { RootState } from 'data/root.reducers';
import { FieldUpdateHandlerParam } from 'shared/form';
import * as securityPolicyActions from 'data/securityPolicy/securityPolicy.actions';
import * as securityPolicySelectors from 'data/securityPolicy/securityPolicy.selectors';
import * as securityPolicyTypes from 'data/securityPolicy/securityPolicy.types';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import {
  MAX_ACCOUNT_LOGIN_ATTEMPTS_DEFAULT,
  ACCOUNT_LOGIN_ATTEMPTS_INTERVAL_MINUTES_DEFAULT,
  ACCOUNT_LOCK_DURATION_MINUTES_DEFAULT,
  APPLY_TO_SYSTEM_ADM_ACCOUNT_DEFAULT,
  MAX_IP_ADDRESS_LOGIN_ATTEMPTS_DEFAULT,
  IP_ADDRESS_LOGIN_ATTEMPTS_INTERVAL_MINUTES_DEFAULT,
  IP_ADDRESS_BLOCK_DURATION_MINUTES_DEFAULT
} from 'routes/SecurityPolicy/securityPolicyConstants';
import { Ref } from '../../components/SecurityPolicyForm/SecurityPolicyForm';
import {
  adaptTemplateVisibilityMode,
  adaptSecurityPolicyData,
  adaptSecurityPolicyTemplate,
} from '../../utils/securityPolicyTemplate.adapter';
import {
  getSecurityPolicyDataToSubmit,
  getSecurityPolicyAuditDetailsToSubmit,
} from '../../utils/securityPolicyDataToSubmit';
import { securityPolicyTemplate } from '../../templates';
import SecurityPolicyForm from '../../components/SecurityPolicyForm';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  deliveryFunctionId: state.global.selectedDF,
  securityPolicyData: securityPolicySelectors.getSecurityPolicy(state),
  securityPolicyErrors: securityPolicySelectors.getSecurityPolicyErrors(state),
  uiState: securityPolicySelectors.getUiState(state),
  formTemplate: adaptTemplateVisibilityMode(
    securityPolicyTemplate,
    'edit',
  ),
});

const mapDispatch = {
  getSecurityPolicy: securityPolicyActions.getSecurityPolicy,
  goToSecurityPolicyView: securityPolicyActions.goToSecurityPolicyView,
  updateSecurityPolicy: securityPolicyActions.updateSecurityPolicy,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SecurityPolicyEdit = ({
  history,
  securityPolicyData,
  deliveryFunctionId,
  formTemplate,
  isAuditEnabled,
  userId,
  userName,
  uiState,
  getSecurityPolicy,
  goToSecurityPolicyView,
  updateSecurityPolicy,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [template, setTemplate] = useState(formTemplate);
  const [formData, setFormData] = useState<Partial<{
      passwordSettings: securityPolicyTypes.Password,
      accountLockoutSettings: securityPolicyTypes.AccountLockout,
      ipAddressBlockingSettings: securityPolicyTypes.IpAddressBlocking,
    }>>({});
  const formRef = useRef<Ref>(null);

  useEffect(() => {
    if (deliveryFunctionId == null) {
      return;
    }
    getSecurityPolicy(deliveryFunctionId)
  }, [deliveryFunctionId, getSecurityPolicy]);

  useEffect(() => {
    const clonedSecurityPolicy: securityPolicyTypes.SecurityPolicyAdaptedData = cloneDeep(adaptSecurityPolicyData(securityPolicyData));
    const newFormData: Partial<{
        passwordSettings: securityPolicyTypes.Password,
        accountLockoutSettings: securityPolicyTypes.AccountLockout,
        ipAddressBlockingSettings: securityPolicyTypes.IpAddressBlocking,
      }> = {};
    newFormData.passwordSettings = {
      id: clonedSecurityPolicy.id,
      minimumPasswordLength: clonedSecurityPolicy.minimumPasswordLength,
      uniquePasswordHistoryCount: clonedSecurityPolicy.uniquePasswordHistoryCount,
      passwordExpirationDays: clonedSecurityPolicy.passwordExpirationDays,
      requireUppercase: clonedSecurityPolicy.requireUppercase,
      requireLowercase: clonedSecurityPolicy.requireLowercase,
      requireNumber: clonedSecurityPolicy.requireNumber,
      requireSymbol: clonedSecurityPolicy.requireSymbol,
      allowNumericSequences: clonedSecurityPolicy.allowNumericSequences,
      maxConsecutiveCharacters: clonedSecurityPolicy.maxConsecutiveCharacters,
      allowPasswordUsernameMatch: clonedSecurityPolicy.allowPasswordUsernameMatch,
      allowCurrentPasswordReuse: clonedSecurityPolicy.allowCurrentPasswordReuse,
    }

    newFormData.accountLockoutSettings = {
      id: clonedSecurityPolicy.id,
      enableAccountLockout: clonedSecurityPolicy.enableAccountLockout,
      maxAccountLoginAttempts: clonedSecurityPolicy.maxAccountLoginAttempts,
      accountLoginAttemptsIntervalMinutes: clonedSecurityPolicy.accountLoginAttemptsIntervalMinutes,
      accountLockDurationMinutes: clonedSecurityPolicy.accountLockDurationMinutes,
      applyToSystemAdminAccount: clonedSecurityPolicy.applyToSystemAdminAccount,
    }

    newFormData.ipAddressBlockingSettings = {
      id: clonedSecurityPolicy.id,
      enableIpAddressBlocking: clonedSecurityPolicy.enableIpAddressBlocking,
      maxIpAddressLoginAttempts: clonedSecurityPolicy.maxIpAddressLoginAttempts,
      ipAddressLoginAttemptsIntervalMinutes: clonedSecurityPolicy.ipAddressLoginAttemptsIntervalMinutes,
      ipAddressBlockDurationMinutes: clonedSecurityPolicy.ipAddressBlockDurationMinutes,
    }

    let clonedTemplate = adaptSecurityPolicyTemplate(formTemplate, 'enableAccountLockout', clonedSecurityPolicy.enableAccountLockout);
    clonedTemplate = adaptSecurityPolicyTemplate(clonedTemplate, 'enableIpAddressBlocking', clonedSecurityPolicy.enableIpAddressBlocking);

    setFormData(newFormData);
    setTemplate(clonedTemplate);
  }, [securityPolicyData]);

  const handleClickCancel = () => {
    goToSecurityPolicyView(history);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam, formSection?: 'passwordSettings' | 'accountLockoutSettings' | 'ipAddressBlockingSettings') => {
    if (template == null || formData == null || formData.passwordSettings == null || formData.accountLockoutSettings == null || formData.ipAddressBlockingSettings == null) {
      return;
    }

    const clonedTemplate = adaptSecurityPolicyTemplate(template, fieldName, value.toString());

    let passwordSettingsData = cloneDeep(formData.passwordSettings) as securityPolicyTypes.Password;
    let accountLockoutSettingsData = cloneDeep(formData.accountLockoutSettings) as securityPolicyTypes.AccountLockout;
    let ipAddressBlockingSettingsData = cloneDeep(formData.ipAddressBlockingSettings) as securityPolicyTypes.IpAddressBlocking;

    if (formSection === 'passwordSettings') {
      passwordSettingsData = {
        ...passwordSettingsData,
        [fieldName]: value,
      };
    } else if (formSection === 'accountLockoutSettings') {
      let accountLockoutDefaults = {};
      if (fieldName === 'enableAccountLockout') {
        accountLockoutDefaults = {
          maxAccountLoginAttempts: MAX_ACCOUNT_LOGIN_ATTEMPTS_DEFAULT,
          accountLoginAttemptsIntervalMinutes: ACCOUNT_LOGIN_ATTEMPTS_INTERVAL_MINUTES_DEFAULT,
          accountLockDurationMinutes: ACCOUNT_LOCK_DURATION_MINUTES_DEFAULT,
          applyToSystemAdminAccount: APPLY_TO_SYSTEM_ADM_ACCOUNT_DEFAULT,
        }
      }
      accountLockoutSettingsData = {
        ...accountLockoutSettingsData,
        [fieldName]: value,
        ...accountLockoutDefaults,
      }
    } else if (formSection === 'ipAddressBlockingSettings') {
      let ipAddressBlockingDefaults = {};
      if (fieldName === 'enableIpAddressBlocking') {
        ipAddressBlockingDefaults = {
          maxIpAddressLoginAttempts: MAX_IP_ADDRESS_LOGIN_ATTEMPTS_DEFAULT,
          ipAddressLoginAttemptsIntervalMinutes: IP_ADDRESS_LOGIN_ATTEMPTS_INTERVAL_MINUTES_DEFAULT,
          ipAddressBlockDurationMinutes: IP_ADDRESS_BLOCK_DURATION_MINUTES_DEFAULT,
        }
      }
      ipAddressBlockingSettingsData = {
        ...ipAddressBlockingSettingsData,
        [fieldName]: value,
        ...ipAddressBlockingDefaults,
      }
    }

    setTemplate(clonedTemplate);
    setFormData({
      passwordSettings: passwordSettingsData,
      accountLockoutSettings: accountLockoutSettingsData,
      ipAddressBlockingSettings: ipAddressBlockingSettingsData,
    });
  };

  const handleClickSave = () => {
    const formData = formRef.current?.getValues();
    const isValid = formRef.current?.isValid(true, true);

    if (!formData || !isValid) {
      return;
    }

    const securityPolicyFormData = getSecurityPolicyDataToSubmit(formData as securityPolicyTypes.SecurityPolicyFormData);

    const securityPolicyAuditDetails = getSecurityPolicyAuditDetailsToSubmit(
      securityPolicyFormData,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    const securityPolicyPayload = {
      data: securityPolicyFormData,
      auditDetails: securityPolicyAuditDetails
    }

    updateSecurityPolicy(deliveryFunctionId, securityPolicyPayload, history);
  };

  const renderForm = () => {
    if (!template || uiState.isLoading) {
      return null;
    }

    return (
      <div>
        <SecurityPolicyForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      </div>
    );
  };

  const renderFormPanel = () => {
    return (
      <FormPanel title={t('securityPolicy.edit.title')}>
        <FormButtons
          isEditing
          onClickSave={handleClickSave}
          onClickCancel={handleClickCancel}
        />
      </FormPanel>
    );
  };

  if (uiState.isLoading) {
    return <div />;
  }

  return (
    <div data-testid="SecurityPolicyEdit">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          {renderForm()}
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SecurityPolicyEdit));
